import {
  enableDelivery,
  disableDelivery,
  ITymberEcommStore,
  updateStore,
} from '@getgreenline/blaze-ecomm'
import { Switch, message } from 'antd'
import { inject } from 'mobx-react'
import React, { FC, useState } from 'react'
import { parseErrorMsg } from '../../../../utilities/helpers'
import Endpoints from '../../../../constants/Endpoints'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { LocationWithConfig } from './EcommerceSettingModels'

interface Props {
  location: LocationWithConfig
  isWaitingBlazeEcomUpdate: boolean
  isUserBlazeAdmin?: boolean
  locallyUpdateConfig: (updatedConfig: ITymberEcommStore) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

export const BlazeAdminDeliveryToggle: FC<Props> = inject('currentCompanyStore')(
  ({
    isWaitingBlazeEcomUpdate,
    isUserBlazeAdmin,
    location,
    locallyUpdateConfig,
    currentCompanyStore,
  }: Props & InjectedProps) => {
    const isDeliveryEnabled = location?.config?.greenlineAttributes?.isDeliveryEnabled
    const isRetailerDeliveryEnabled = location?.isEcommDeliveryEnabled
    const [isLoading, setIsLoading] = useState(false)
    const isBlazeEcomEnabled = location?.config?.attributes.isActive
    const isDisabled = isWaitingBlazeEcomUpdate || !isUserBlazeAdmin || !isBlazeEcomEnabled

    const disableRetailerDelivery = async () => {
      if (isBlazeEcomEnabled) {
        await updateStore(Endpoints.AUTHZ_GATEWAY_URL, `${location.companyId}`, `${location.id}`, {
          isActive: isBlazeEcomEnabled,
          allowDeliveries: false,
        })
        await currentCompanyStore?.editLocation({
          id: location.id,
          isEcommDeliveryEnabled: false,
        })
      }
    }

    const toggleIsEcommDeliveryEnabled = async () => {
      if (!location) return
      setIsLoading(true)
      try {
        if (!isBlazeEcomEnabled) {
          throw new Error('Ecom is disabled for this location')
        }

        if (!isDeliveryEnabled) {
          await enableDelivery(
            Endpoints.AUTHZ_GATEWAY_URL,
            `${location.companyId}`,
            `${location.id}`,
          )
        } else {
          await disableDelivery(
            Endpoints.AUTHZ_GATEWAY_URL,
            `${location.companyId}`,
            `${location.id}`,
          )
          if (isRetailerDeliveryEnabled) {
            await disableRetailerDelivery()
          }
        }

        if (!location.config) {
          throw new Error('No config for this location')
        }
        const updatedConfig: ITymberEcommStore = {
          ...location.config,
          greenlineAttributes: {
            ...location.config.greenlineAttributes,
            isDeliveryEnabled: !isDeliveryEnabled,
          },
        }
        locallyUpdateConfig(updatedConfig)
        message.success(`Delivery for ${location?.name} successfully updated.`, 2)
      } catch (error) {
        message.error(
          parseErrorMsg(error) ||
            `There was an error while updating Delivery for ${location?.name}`,
        )
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <Switch
        checked={isDeliveryEnabled}
        disabled={isDisabled}
        loading={isLoading}
        onChange={toggleIsEcommDeliveryEnabled}
      />
    )
  },
)
